import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, Text, StyleSheet, type ViewStyle } from 'react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY, Z_INDEX, withWeight } from '../../theme';
import { DialogPortal } from './DialogPortal';
import type { TooltipProps } from './Tooltip';

const HOVER_DELAY_MS = 400;
const TOOLTIP_GAP = 6;
// Rough label height + gap — if there isn't this much room above the
// anchor, the label opens beside it instead.
const MIN_ROOM_ABOVE = 32;

interface AnchorRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * Web-only hover/focus tooltip for icon-only controls that have no visible
 * text label (map zoom/locate/layers, the plan panel's switcher/close
 * buttons). Portals to document.body via DialogPortal so a clipping scroll
 * container never cuts it off — the same problem SidebarNav's rail tooltip
 * solves by drawing outside its ScrollView, but this component is for
 * controls outside the sidebar tree entirely.
 *
 * The wrapper renders with `display: contents` so it adds no box of its own
 * — wrapping a flex child in this can't change its sizing — and it never
 * sets pointerEvents, so clicks reach the child exactly as before.
 */
export function Tooltip({ label, shortcut, children }: TooltipProps) {
  const { colors } = useTheme();
  const anchorRef = useRef<View>(null);
  const [visible, setVisible] = useState(false);
  const [rect, setRect] = useState<AnchorRect | null>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const measure = useCallback((): AnchorRect | null => {
    // The wrapper is display: contents, so it has no box of its own and its
    // rect is all zeros. Measure the wrapped control instead.
    const wrapper = anchorRef.current as unknown as HTMLElement | null;
    const node = (wrapper?.firstElementChild as HTMLElement | null) ?? wrapper;
    if (typeof node?.getBoundingClientRect !== 'function') return null;
    const r = node.getBoundingClientRect();
    return { top: r.top, left: r.left, width: r.width, height: r.height };
  }, []);

  const show = useCallback(() => {
    const r = measure();
    if (r) setRect(r);
    setVisible(true);
  }, [measure]);

  const hide = useCallback(() => setVisible(false), []);

  const clearHoverTimer = () => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  };

  const handlePointerEnter = useCallback(() => {
    clearHoverTimer();
    hoverTimer.current = setTimeout(show, HOVER_DELAY_MS);
  }, [show]);

  const handlePointerLeave = useCallback(() => {
    clearHoverTimer();
    hide();
  }, [hide]);

  // Keyboard focus shows it immediately — no reason to make a keyboard user
  // wait out the hover delay.
  const handleFocus = useCallback(() => {
    clearHoverTimer();
    show();
  }, [show]);

  const handleBlur = useCallback(() => {
    clearHoverTimer();
    hide();
  }, [hide]);

  useEffect(() => () => clearHoverTimer(), []);

  const above = rect ? rect.top > MIN_ROOM_ABOVE : true;
  const positionStyle = rect
    ? above
      ? {
          top: rect.top - TOOLTIP_GAP,
          left: rect.left + rect.width / 2,
          transform: [{ translateX: '-50%' }, { translateY: '-100%' }],
        }
      : {
          top: rect.top + rect.height / 2,
          left: rect.left + rect.width + TOOLTIP_GAP,
          transform: [{ translateY: '-50%' }],
        }
    : null;

  return (
    <View
      ref={anchorRef}
      // display: 'contents' (a web-only CSS value RN's ViewStyle type still
      // accepts) makes this wrapper add no box of its own, so it can't
      // change the anchor's flex sizing.
      style={{ display: 'contents' }}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}>
      {children}
      {visible && positionStyle && (
        <DialogPortal>
          <View
            pointerEvents="none"
            testID="tooltip"
            // Three web-only things RN's types don't cover: position
            // 'fixed', the string percentage translate that centers on the
            // anchor, and react-native-web's dataSet (drives the fade-in
            // keyframe in motion.ts's MOTION_CSS_VARS). Casts rather than
            // expect-error comments, because which of them errors depends on
            // the generated expo-env.d.ts, and CI doesn't have that file.
            style={
              [
                styles.tooltip,
                { backgroundColor: colors.action, position: 'fixed' },
                positionStyle,
              ] as unknown as ViewStyle[]
            }
            {...({ dataSet: { rediAnim: 'tooltip-in' } } as object)}>
            <Text style={[styles.label, { color: colors.actionOn }]} numberOfLines={1}>
              {label}
              {shortcut ? ` (${shortcut})` : ''}
            </Text>
          </View>
        </DialogPortal>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  tooltip: {
    paddingVertical: SPACING.xxs,
    paddingHorizontal: SPACING.sm,
    borderRadius: BORDER_RADIUS.sm,
    zIndex: Z_INDEX.toast,
  },
  label: {
    ...withWeight(TYPOGRAPHY.caption, 'semibold'),
  },
});
